import axios from 'axios'
import {
  LIVE_REPORTING_VERSION,
  type LiveReportPinRequest,
  type LiveReportResult,
  type LiveReportSummaryPage,
} from 'src/queries/liveReporting/liveReporting.models'
import {
  parseLiveReportResult,
  parseLiveReportSummaryPage,
} from 'src/queries/liveReporting/liveReporting.validation'
import { reportingIdentifier } from 'src/queries/reporting/reporting.identifier'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'

export interface PinLiveReportingReportRequest {
  candidateId: string
  title: string
  idempotencyKey: string
  signal?: AbortSignal
}

export interface ListLiveReportingReportsRequest {
  limit?: number
  after?: string
  signal?: AbortSignal
}

export interface LiveReportingResultRequest {
  reportId: string
  signal?: AbortSignal
}

const nonEmpty = (value: string, name: string): string => {
  if (
    typeof value !== 'string' ||
    value.trim() !== value ||
    value.length === 0
  ) {
    throw new Error(`${name} must be a non-empty trimmed string`)
  }
  return value
}

const reportTitle = (value: string): string => {
  const result = nonEmpty(value, 'title')
  if (result.length > 256)
    throw new Error('title must be at most 256 characters')
  return result
}

const identifier = (value: string, name: string): string => {
  return reportingIdentifier(value, name)
}

const idempotency = (value: string): string => {
  const result = nonEmpty(value, 'idempotencyKey')
  if (result.length > 256) {
    throw new Error('idempotencyKey must be at most 256 characters')
  }
  return result
}

const requestConfig = (idempotencyKey?: string, signal?: AbortSignal) => {
  const config = getRequestConfig(RequestConfig.Default)
  return {
    ...config,
    ...(signal === undefined ? {} : { signal }),
    ...(idempotencyKey === undefined
      ? {}
      : {
          headers: {
            ...config.headers,
            'Idempotency-Key': idempotency(idempotencyKey),
          },
        }),
  }
}

const pagination = (limit?: number, after?: string) => {
  if (
    limit !== undefined &&
    (!Number.isInteger(limit) || limit < 1 || limit > 12)
  ) {
    throw new Error('limit must be between 1 and 12')
  }
  if (after !== undefined) identifier(after, 'after')
  return {
    ...(limit === undefined ? {} : { limit }),
    ...(after === undefined ? {} : { after }),
  }
}

export const pinLiveReportingReport = async ({
  candidateId,
  title: reportTitleValue,
  idempotencyKey,
  signal,
}: PinLiveReportingReportRequest): Promise<void> => {
  const body: LiveReportPinRequest = {
    version: LIVE_REPORTING_VERSION,
    candidate_id: identifier(candidateId, 'candidateId'),
    title: reportTitle(reportTitleValue),
  }
  const response = await axios.post(
    '/reporting/live-reports',
    body,
    requestConfig(idempotencyKey, signal),
  )
  // Axios represents an empty 204 body differently between adapters (undefined
  // in tests and an empty string in browsers). HTTP 204 is the contract; do
  // not parse or infer a response payload from it.
  if (response.status !== 204) {
    throw new Error('live report pin must return HTTP 204')
  }
}

export const fetchLiveReportSummaries = async ({
  limit,
  after,
  signal,
}: ListLiveReportingReportsRequest = {}): Promise<LiveReportSummaryPage> => {
  const response = await axios.get('/reporting/live-reports', {
    ...requestConfig(undefined, signal),
    params: pagination(limit, after),
  })
  return parseLiveReportSummaryPage(response.data)
}

export const fetchLiveReportResult = async ({
  reportId,
  signal,
}: LiveReportingResultRequest): Promise<LiveReportResult> => {
  const response = await axios.get(
    `/reporting/live-reports/${identifier(reportId, 'reportId')}/result`,
    requestConfig(undefined, signal),
  )
  return parseLiveReportResult(response.data)
}
